
import React, { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ApiResponseDisplayProps {
  response: any;
}

export const ApiResponseDisplay: React.FC<ApiResponseDisplayProps> = ({ response }) => {
  const [showRawJson, setShowRawJson] = useState(false);
  
  if (!response) return null;
  
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };
  
  // Special handling for subscription renewal response
  const hasRenewalStats = response.pre_renewal || response.post_renewal;
  
  return (
    <div className="rounded-md border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 p-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="font-medium">API Response</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRawJson(!showRawJson)}
          >
            {showRawJson ? 'Show Formatted' : 'Show Raw JSON'}
          </Button>
        </div>
      </div>
      
      {showRawJson ? (
        <pre className="p-4 bg-gray-900 text-gray-100 overflow-auto text-xs">
          {JSON.stringify(response, null, 2)}
        </pre>
      ) : (
        <div className="p-4 space-y-4">
          {/* Display renewal specific information */}
          {hasRenewalStats && (
            <>
              <Collapsible className="border rounded-md">
                <CollapsibleTrigger className="flex w-full items-center justify-between p-3 font-medium">
                  <span>Pre-Renewal State</span>
                  <ChevronDown className="h-4 w-4" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-3 px-4 pt-0 text-sm">
                  {response.pre_renewal ? (
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(response.pre_renewal).map(([key, value]) => (
                        <div key={key} className="py-1">
                          <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {String(value)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No pre-renewal data available</p>
                  )}
                </CollapsibleContent>
              </Collapsible>
              
              <Collapsible className="border rounded-md">
                <CollapsibleTrigger className="flex w-full items-center justify-between p-3 font-medium">
                  <span>Post-Renewal State</span>
                  <ChevronDown className="h-4 w-4" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-3 px-4 pt-0 text-sm">
                  {response.post_renewal ? (
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(response.post_renewal).map(([key, value]) => (
                        <div key={key} className="py-1">
                          <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {
                            Array.isArray(value) 
                              ? value.length > 0 ? value.join(', ') : 'None'
                              : String(value)
                          }
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No post-renewal data available</p>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
          
          {/* Display other response fields */}
          {Object.entries(response)
            .filter(([key]) => !['pre_renewal', 'post_renewal', 'status', 'message', 'timestamp'].includes(key))
            .map(([key, value]) => (
              <div key={key} className="border-t pt-3 first:border-t-0 first:pt-0">
                <div className="font-medium mb-1">{key.replace(/_/g, ' ')}:</div>
                <div className="text-sm">
                  {typeof value === 'object' ? (
                    <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto">
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  ) : (
                    String(value)
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
